// @greenlight-spec: 28-it-dashboard-ui.md#internal-health--internal-health-org-admin
// @greenlight-spec: 11-observability.md#metric-history
import * as React from 'react';

import type { HealthSeries, HealthSeriesServiceKey } from '@greenlight/shared';

import {
  HISTORY_RANGES,
  historyWindow,
  incidentMarkers,
  releaseMarkers,
  type HistoryMarker,
  type HistoryRange,
  type HistoryWindow,
} from './historyModel';
import { useSeries, useWindowIncidents, type WindowIncidents } from './useHealthHistory';

const RANGE_KEY = 'gl-health-history-range';

/**
 * A panel's range, kept per panel for the session so each card reopens on the
 * window its reader last chose for it. Not kept across a reload.
 */
export function useStoredRange(panel: string): [HistoryRange, (range: HistoryRange) => void] {
  const key = `${RANGE_KEY}:${panel}`;
  const [range, setRangeState] = React.useState<HistoryRange>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored !== null && stored in HISTORY_RANGES ? (stored as HistoryRange) : '24h';
    } catch {
      return '24h';
    }
  });
  const setRange = React.useCallback(
    (next: HistoryRange): void => {
      setRangeState(next);
      try {
        sessionStorage.setItem(key, next);
      } catch {
        // Storage unavailable; the in-memory choice still holds for this visit.
      }
    },
    [key],
  );
  return [range, setRange];
}

export interface HistoryContextValue {
  range: HistoryRange;
  setRange: (range: HistoryRange) => void;
  span: HistoryWindow;
  markers: HistoryMarker[];
  incidents: WindowIncidents;
  releasesFailed: boolean;
  /** A series every collection pass writes, so its coverage is the panel's coverage. */
  coverage: HealthSeries | undefined;
  /** The one service this panel charts; unset for a card about the whole install. */
  service?: HealthSeriesServiceKey;
}

const HistoryContext = React.createContext<HistoryContextValue | null>(null);

/**
 * One History panel's window, markers and coverage, shared by the charts in
 * it so a marker lands at the same moment on each. The range never touches
 * the current readings the card leads with.
 */
export function HistoryProvider({
  range,
  setRange,
  service,
  children,
}: {
  range: HistoryRange;
  setRange: (range: HistoryRange) => void;
  service?: HealthSeriesServiceKey;
  children: React.ReactNode;
}): React.ReactElement {
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const span = React.useMemo(() => historyWindow(range, nowMs), [range, nowMs]);

  // A service's own readiness is written on every pass that could read it, so
  // its coverage and start of history are that service's, not the install's.
  const coverage = useSeries(
    service ? 'availability_ready_replicas' : 'queue_pending',
    span,
    service,
  ).data;
  const releaseSeries = useSeries('release_running', span);
  const windowIncidents = useWindowIncidents(span);
  const incidents = React.useMemo(
    () =>
      service
        ? {
            ...windowIncidents,
            items: windowIncidents.items.filter((incident) => incident.tier === service),
          }
        : windowIncidents,
    [windowIncidents, service],
  );
  const markers = React.useMemo(
    () => [
      ...incidentMarkers(incidents.items),
      ...(releaseSeries.data ? releaseMarkers(releaseSeries.data) : []),
    ],
    [incidents.items, releaseSeries.data],
  );

  const value = React.useMemo<HistoryContextValue>(
    () => ({
      range,
      setRange,
      span,
      markers,
      incidents,
      releasesFailed: releaseSeries.isError,
      coverage,
      ...(service ? { service } : {}),
    }),
    [range, setRange, span, markers, incidents, releaseSeries.isError, coverage, service],
  );
  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
}

export function useHistory(): HistoryContextValue {
  const value = React.useContext(HistoryContext);
  if (!value) throw new Error('useHistory must be used inside a HistoryProvider');
  return value;
}
